import { supabase } from '../supabase';

export interface DbCopyVariant {
  id: string;
  vacancy_id: string;
  title: string;
  body: string;
  source: 'AI' | 'MANUAL';
  selected: boolean;
  status: 'pending' | 'approved' | 'rejected';
  feedback: string;
  created_at: string;
  updated_at: string;
}

export interface DbVisual {
  id: string;
  vacancy_id: string;
  title: string;
  url: string;
  status: 'UPLOADED' | 'APPROVED' | 'REJECTED';
  selected: boolean;
  feedback: string;
  created_at: string;
  updated_at: string;
}

export interface DbVisualVersion {
  id: string;
  visual_id: string;
  version_number: number;
  url: string;
  uploaded_at: string;
  uploaded_by?: string;
  status: 'UPLOADED' | 'APPROVED' | 'REJECTED';
}

export interface DbFeedbackItem {
  id: string;
  visual_id?: string;
  copy_variant_id?: string;
  feedback_text: string;
  status: 'open' | 'in_progress' | 'resolved';
  created_by?: string;
  created_at: string;
  resolved_at?: string;
}

export interface DbFeedbackResolution {
  id: string;
  feedback_item_id: string;
  visual_version_id: string;
  resolved_at: string;
}

export interface DbUpdateNotification {
  id: string;
  vacancy_id: string;
  visual_id: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

export async function createCopyVariant(
  vacancyId: string,
  data: {
    title: string;
    body: string;
    source: 'AI' | 'MANUAL';
  }
): Promise<DbCopyVariant | null> {
  const { data: result, error } = await supabase
    .from('copy_variants')
    .insert({
      vacancy_id: vacancyId,
      title: data.title,
      body: data.body,
      source: data.source
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating copy variant:', error);
    return null;
  }

  return result;
}

export async function getCopyVariantsByVacancy(vacancyId: string): Promise<DbCopyVariant[]> {
  const { data, error } = await supabase
    .from('copy_variants')
    .select('*')
    .eq('vacancy_id', vacancyId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching copy variants:', error);
    return [];
  }

  return data || [];
}

export async function updateCopyVariant(
  id: string,
  updates: Partial<{
    title: string;
    body: string;
    source: 'AI' | 'MANUAL';
    selected: boolean;
    status: 'pending' | 'approved' | 'rejected';
    feedback: string;
  }>
): Promise<DbCopyVariant | null> {
  const { data, error } = await supabase
    .from('copy_variants')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating copy variant:', error);
    return null;
  }

  return data;
}

export async function createVisual(
  vacancyId: string,
  data: {
    title: string;
    url: string;
  }
): Promise<DbVisual | null> {
  const { data: result, error } = await supabase
    .from('visuals')
    .insert({
      vacancy_id: vacancyId,
      title: data.title,
      url: data.url
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating visual:', error);
    return null;
  }

  return result;
}

export async function getVisualsByVacancy(vacancyId: string): Promise<DbVisual[]> {
  const { data, error } = await supabase
    .from('visuals')
    .select('*')
    .eq('vacancy_id', vacancyId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching visuals:', error);
    return [];
  }

  return data || [];
}

export async function updateVisual(
  id: string,
  updates: Partial<{
    title: string;
    url: string;
    status: 'UPLOADED' | 'APPROVED' | 'REJECTED';
    selected: boolean;
    feedback: string;
  }>
): Promise<DbVisual | null> {
  const { data, error } = await supabase
    .from('visuals')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating visual:', error);
    return null;
  }

  return data;
}

export async function createFeedbackItem(
  visualId: string,
  feedbackText: string
): Promise<DbFeedbackItem | null> {
  const { data, error } = await supabase
    .from('feedback_items')
    .insert({
      visual_id: visualId,
      feedback_text: feedbackText,
      status: 'open'
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating feedback item:', error);
    return null;
  }

  return data;
}

export async function createCopyFeedbackItem(
  copyVariantId: string,
  feedbackText: string
): Promise<DbFeedbackItem | null> {
  const { data, error } = await supabase
    .from('feedback_items')
    .insert({
      copy_variant_id: copyVariantId,
      feedback_text: feedbackText,
      status: 'open'
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating copy feedback item:', error);
    return null;
  }

  return data;
}

export async function getFeedbackItemsByVisual(visualId: string): Promise<DbFeedbackItem[]> {
  const { data, error } = await supabase
    .from('feedback_items')
    .select('*')
    .eq('visual_id', visualId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching feedback items:', error);
    return [];
  }

  return data || [];
}

export async function getFeedbackItemsByCopyVariant(copyVariantId: string): Promise<DbFeedbackItem[]> {
  const { data, error } = await supabase
    .from('feedback_items')
    .select('*')
    .eq('copy_variant_id', copyVariantId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching copy feedback items:', error);
    return [];
  }

  return data || [];
}

export async function updateFeedbackItem(
  id: string,
  updates: Partial<{
    status: 'open' | 'in_progress' | 'resolved';
    resolved_at: string;
  }>
): Promise<DbFeedbackItem | null> {
  const { data, error } = await supabase
    .from('feedback_items')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating feedback item:', error);
    return null;
  }

  return data;
}

export async function createVisualVersion(
  visualId: string,
  url: string,
  versionNumber: number
): Promise<DbVisualVersion | null> {
  const { data, error } = await supabase
    .from('visual_versions')
    .insert({
      visual_id: visualId,
      version_number: versionNumber,
      url: url,
      status: 'UPLOADED'
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating visual version:', error);
    return null;
  }

  return data;
}

export async function getVisualVersionsByVisual(visualId: string): Promise<DbVisualVersion[]> {
  const { data, error } = await supabase
    .from('visual_versions')
    .select('*')
    .eq('visual_id', visualId)
    .order('version_number', { ascending: false });

  if (error) {
    console.error('Error fetching visual versions:', error);
    return [];
  }

  return data || [];
}

export async function getLatestVersionNumber(visualId: string): Promise<number> {
  const { data, error } = await supabase
    .from('visual_versions')
    .select('version_number')
    .eq('visual_id', visualId)
    .order('version_number', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Error fetching latest version:', error);
    return 0;
  }

  return data?.version_number || 0;
}

export async function createFeedbackResolution(
  feedbackItemId: string,
  visualVersionId: string
): Promise<DbFeedbackResolution | null> {
  const { data, error } = await supabase
    .from('feedback_resolutions')
    .insert({
      feedback_item_id: feedbackItemId,
      visual_version_id: visualVersionId
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating feedback resolution:', error);
    return null;
  }

  return data;
}

export async function getResolvedFeedbackForVersion(
  visualVersionId: string
): Promise<DbFeedbackItem[]> {
  const { data, error } = await supabase
    .from('feedback_resolutions')
    .select('feedback_items(*)')
    .eq('visual_version_id', visualVersionId);

  if (error) {
    console.error('Error fetching resolved feedback:', error);
    return [];
  }

  return data?.map((r: any) => r.feedback_items).filter(Boolean) || [];
}

export async function createUpdateNotification(
  vacancyId: string,
  visualId: string,
  message: string
): Promise<DbUpdateNotification | null> {
  const { data, error } = await supabase
    .from('update_notifications')
    .insert({
      vacancy_id: vacancyId,
      visual_id: visualId,
      message: message,
      is_read: false
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating notification:', error);
    return null;
  }

  return data;
}

export async function getNotificationsByVacancy(vacancyId: string): Promise<DbUpdateNotification[]> {
  const { data, error } = await supabase
    .from('update_notifications')
    .select('*')
    .eq('vacancy_id', vacancyId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching notifications:', error);
    return [];
  }

  return data || [];
}

export async function markNotificationAsRead(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('update_notifications')
    .update({ is_read: true })
    .eq('id', id);

  if (error) {
    console.error('Error marking notification as read:', error);
    return false;
  }

  return true;
}

export async function getUnreadNotificationCount(vacancyId: string): Promise<number> {
  const { count, error } = await supabase
    .from('update_notifications')
    .select('*', { count: 'exact', head: true })
    .eq('vacancy_id', vacancyId)
    .eq('is_read', false);

  if (error) {
    console.error('Error fetching unread count:', error);
    return 0;
  }

  return count || 0;
}
